import type { AnalyticsEvent } from '@seek/adv-header-footer';
import type { Locale } from '@seek/melways-sites';
import { useEffect } from 'react';

import { getContractTrackingData } from 'src/modules/AnalyticsTracker/utils/getContractTrackingData';
import {
  useTalentSearchContext,
  useDashboardJobs,
  useHirerAccount,
  useHirerContracts,
} from 'src/modules/ApiClient';
import type {
  DashboardAccountContext,
  PageData,
  EmployerSiteName,
  AdvertiserContractTrackingData,
  ErrorPageData,
} from 'src/types';
import { isTrackingEnabled } from 'src/utils/config';

import { constructPageTrackingData } from './utils/constructPageTrackingData';
import getPageTrackingData from './utils/getPageTrackingData';
import setPageTrackingData from './utils/setPageTrackingData';
import trackLink from './utils/trackLink';
import trackView from './utils/trackView';

// eg: trackPageLoad('home-page-loaded, TRACKING_PAGE_DATA);
export function trackPageLoad(
  eventName: string | string[],
  pageData: PageData | ErrorPageData,
  contractData?: AdvertiserContractTrackingData,
) {
  if (isTrackingEnabled()) {
    const viewData = {
      ...pageData,
      ...contractData,
    };
    trackView(eventName, viewData);
    setPageTrackingData(pageData);
  }
}

// eg: trackPageInteractions('register-button-clicked', 'registration-form');
export function trackPageInteraction(
  eventName: string | string[],
  actionOrigin?: string,
  eventData?: any,
) {
  if (isTrackingEnabled()) {
    const currentPageTrackingData = getPageTrackingData();
    trackLink(eventName, {
      actionOrigin,
      ...(eventData || {}),
      ...currentPageTrackingData,
    });
  }
}

// eg: trackFormFieldInteraction('click-away-from-field, 'registration-form', 'email');
export function trackFormFieldInteraction(
  eventName: string,
  formName: string,
  fieldId: string,
) {
  if (isTrackingEnabled()) {
    const currentPageTrackingData = getPageTrackingData();
    trackLink(eventName, {
      actionOrigin: formName,
      objectInteraction: fieldId,
      ...currentPageTrackingData,
    });
  }
}

// eg: trackFormErrors('form-incomplete', 'registration-form', invalidFieldsArray);
// eg: trackFormErrors('email-already-exists', 'registration-form');
export function trackFormErrors(
  errorMessage: string,
  formName: string,
  invalidFields: string[] = [],
) {
  if (isTrackingEnabled()) {
    const currentPageTrackingData = getPageTrackingData();
    trackLink('error-message-shown', {
      actionOrigin: formName,
      errorMessage,
      invalidFormFields: [...invalidFields],
      ...currentPageTrackingData,
    });
  }
}

interface Props {
  accountContext: DashboardAccountContext | null;
  site: EmployerSiteName;
  locale: Locale;
}

export const PageLoadTracker = ({ accountContext, site, locale }: Props) => {
  const { loading: tsContextLoading, data: tsContext } =
    useTalentSearchContext();
  const { loading: jobsLoading, data: jobsInfo } = useDashboardJobs();
  const { loading: hirerAccountLoading, data: hirerAccount } =
    useHirerAccount();
  const { loading: hirerContractsLoading, data: hirerContracts } =
    useHirerContracts();

  // TODO: might have to move to get right data in
  useEffect(() => {
    if (
      tsContextLoading ||
      jobsLoading ||
      hirerAccountLoading ||
      hirerContractsLoading ||
      !accountContext ||
      !tsContext ||
      jobsInfo?.dashboardJobs.__typename !== 'DashboardJobsResponseSuccess' ||
      jobsInfo?.dashboardJobsCounts.__typename !==
        'DashboardJobsCountsResponseSuccess'
      // Note: hirerAccount can be null (in that case assume 'fixed' pricing)
      // Note: hirerContracts can be null (in that case assume 'casual' contract)
    ) {
      return;
    }

    trackPageLoad(
      'dashboard_displayed',
      constructPageTrackingData({
        accountContext,
        tsContext,
        dashboardJobs: jobsInfo.dashboardJobs,
        dashboardJobsCounts: jobsInfo.dashboardJobsCounts,
        hirerAccount,
        hirerContracts,
        site,
        locale,
      }),
      getContractTrackingData(hirerContracts),
    );
  });

  return null;
};

export const trackHeaderFooterInteractions = (
  analyticsEvent: AnalyticsEvent,
) => {
  if (isTrackingEnabled()) {
    const currentPageTrackingData = getPageTrackingData();
    trackLink(analyticsEvent.eventName, {
      ...currentPageTrackingData,
      ...analyticsEvent,
    });
  }
};
